import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';
import { getAuthStorage, isStandalone } from '../utils/pwaUtils';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        const storage = getAuthStorage();

        // Clear state first
        setUser(null);
        setToken(null);

        // Clear storage
        storage.removeItem('user');
        storage.removeItem('token');

        // Redirect only if we are in a protected area to force a clean state
        // Avoiding redundant refreshes if already at public root or login
        const publicPaths = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/terms', '/privacy'];
        if (!publicPaths.includes(window.location.pathname)) {
            window.location.href = '/';
        }
    }, []);

    const relogin = useCallback(async (customToken = null) => {
        // Use customToken or fall back to state token
        // If we use state token, we still want to avoid dependency loop,
        // so we could use a functional setToken if needed, but here we just need the value.
        const activeToken = customToken || token;
        if (!activeToken) return;
        try {
            const response = await api.get('/refresh');

            if (response.data.token) {
                const { user: userData, token: newToken } = response.data;
                const storage = getAuthStorage();
                const payload = JSON.parse(atob(newToken.split('.')[1]));
                userData.permissions = payload.permissions || [];
                userData.profileId = payload.profileId;
                userData.role = payload.role || userData.role;
                userData.email = payload.email || userData.email;

                setUser(userData);
                setToken(newToken);
                storage.setItem('user', JSON.stringify(userData));
                storage.setItem('token', newToken);
                return true;
            }
        } catch (error) {
            console.error("Failed to auto-relogin", error);
            if (isStandalone() && error.response?.status === 401) {
                logout();
            }
            return false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logout]); // Removed 'token' from dependencies to avoid loop

    const login = (userData, authToken) => {
        // Decode token payload to get permissions, role and profileId
        try {
            const payload = JSON.parse(atob(authToken.split('.')[1]));
            userData.permissions = payload.permissions || [];
            userData.profileId = payload.profileId;
            userData.role = payload.role || userData.role;
            userData.email = payload.email || userData.email;
        } catch (e) {
            console.error("Failed to decode token permissions", e);
            userData.permissions = [];
        }

        const storage = getAuthStorage();
        setUser(userData);
        setToken(authToken);
        storage.setItem('user', JSON.stringify(userData));
        storage.setItem('token', authToken);
    };

    useEffect(() => {
        const storage = getAuthStorage();
        const storedUser = storage.getItem('user');
        const storedToken = storage.getItem('token');

        // 1. Initial hydration from storage
        if (storedUser && storedToken) {
            try {
                const userObj = JSON.parse(storedUser);
                const payload = JSON.parse(atob(storedToken.split('.')[1]));

                // Sync payload data to user object
                userObj.permissions = payload.permissions || userObj.permissions || [];
                userObj.profileId = payload.profileId || userObj.profileId;
                userObj.role = payload.role || userObj.role;
                userObj.email = payload.email || userObj.email;

                setUser(userObj);
                setToken(storedToken);

                // For standalone/PWA, try to refresh immediately to verify token
                if (isStandalone()) {
                    relogin(storedToken).catch(() => {
                        // Relogin handled by its own 401 logic if needed
                    });
                }
            } catch (e) {
                console.error("Error hydrating user data:", e);
                // If corrupted, clean up
                storage.removeItem('user');
                storage.removeItem('token');
            }
        }

        // 2. Set loading false after initial hydrate attempt
        setLoading(false);

        // 3. Setup 401 interceptor on the unified API instance
        const interceptor = api.interceptors.response.use(
            response => response,
            error => {
                const isAuthRoute = error.config?.url?.includes('/login') ||
                    error.config?.url?.includes('/register') ||
                    error.config?.url?.includes('/forgot-password') ||
                    error.config?.url?.includes('/reset-password') ||
                    error.config?.url?.includes('/refresh');

                if (error.response?.status === 401 && !isAuthRoute) {
                    console.log("[AuthContext] 401 detected. Logging out...");
                    logout();
                }
                return Promise.reject(error);
            }
        );

        return () => {
            api.interceptors.response.eject(interceptor);
        };
    }, [logout, relogin]);

    const value = {
        user,
        token,
        login,
        relogin,
        logout,
        isAuthenticated: !!token
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
